import { 
    collection, 
    addDoc, 
    serverTimestamp, 
    query, 
    where, 
    orderBy, 
    limit, 
    getDocs,
    Timestamp 
} from 'firebase/firestore';
import { db } from '../firebase';
import { DeviceInfo, DeviceInfoService } from './deviceInfoService';

export type AuditEventType = 
    | 'AUTH_LOGIN'
    | 'AUTH_LOGOUT'
    | 'USER_DELETED'
    | 'USER_ROLE_UPDATED'
    | 'USER_STATUS_UPDATED'
    | 'API_KEY_CREATED'
    | 'API_KEY_REVOKED'
    | 'CAMPAIGN_DELETED'
    | 'TEMPLATE_MODIFIED'
    | 'SYSTEM_SETTING_CHANGED'
    | 'SECURITY_ALERT';

export interface AuditLogEntry {
    id?: string;
    timestamp: Timestamp;
    eventType: AuditEventType;
    actorId: string;       // User ID of the person performing the action
    actorEmail: string;    // Email of the person performing the action
    targetId?: string;     // ID of the resource being acted upon (e.g., target userId)
    description: string;   // Human-readable description
    metadata?: Record<string, any>; // Additional technical details
    deviceInfo?: DeviceInfo;
    ipAddress?: string;    // Note: IP usually captured by Cloud Functions, but can be added if available
}

class AuditLogService {
    private logsCollection = collection(db, 'admin_logs');

    /**
     * Record a new audit log entry
     */
    async logEvent(
        eventType: AuditEventType, 
        actorId: string, 
        actorEmail: string, 
        description: string, 
        targetId?: string,
        metadata?: Record<string, any>
    ): Promise<string> {
        try {
            const deviceInfo = await DeviceInfoService.collectDeviceInfo();
            
            const docRef = await addDoc(this.logsCollection, {
                eventType,
                actorId,
                actorEmail,
                targetId: targetId || null,
                description,
                metadata: metadata || {},
                deviceInfo,
                timestamp: serverTimestamp()
            });

            console.log(`[AuditLog] ${eventType}: ${description}`);
            return docRef.id;
        } catch (error) {
            console.error('Failed to write audit log:', error);
            // We don't throw here to avoid breaking the main app flow if logging fails,
            // but in a strict security environment, you might want to.
            return '';
        }
    }

    /**
     * Fetch recent audit logs (Admin only)
     */
    async getRecentLogs(maxCount: number = 100): Promise<AuditLogEntry[]> {
        const q = query(
            this.logsCollection,
            orderBy('timestamp', 'desc'),
            limit(maxCount)
        );

        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        })) as AuditLogEntry[];
    }

    /**
     * Fetch logs for a specific target resource
     */
    async getLogsByTarget(targetId: string): Promise<AuditLogEntry[]> {
        const q = query(
            this.logsCollection,
            where('targetId', '==', targetId),
            orderBy('timestamp', 'desc')
        );

        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        })) as AuditLogEntry[];
    }
}

export const auditLogService = new AuditLogService();
